/**
 * The product's name, in one place so a rename is a one-line change here (plus web/index.html and
 * web/public/manifest.webmanifest, which are static). The wordmark shows the two parts in two tones.
 */
export const APP_WORDMARK = ['Ven', 'Link'] as const;
export const APP_NAME = APP_WORDMARK.join('');

export const PRICING_MODES = ['FIXED', 'MAKE_OFFER', 'FREE'] as const;
export type PricingMode = (typeof PRICING_MODES)[number];

export const ITEM_STATUSES = ['AVAILABLE', 'PENDING', 'SOLD', 'ARCHIVED'] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];

/** Statuses a buyer can see on the public sale page. */
/**
 * Item numbers start here in every sale (#1000, #1001, ...). Items listed before this rule were
 * shifted up by LEGACY_ITEM_NUMBER_OFFSET, so an old link to #3 redirects to #1002.
 */
export const ITEM_NUMBER_START = 1000;
export const LEGACY_ITEM_NUMBER_OFFSET = ITEM_NUMBER_START - 1;

/** Optional item condition, shown to buyers when set. */
export const ITEM_CONDITIONS = ['NEW', 'LIKE_NEW', 'GOOD', 'FAIR', 'FOR_PARTS'] as const;
export type ItemCondition = (typeof ITEM_CONDITIONS)[number];
export const ITEM_CONDITION_LABELS: Record<ItemCondition, string> = {
  NEW: 'New',
  LIKE_NEW: 'Like new',
  GOOD: 'Good',
  FAIR: 'Fair',
  FOR_PARTS: 'For parts',
};

export const PUBLIC_ITEM_STATUSES = ['AVAILABLE', 'PENDING', 'SOLD'] as const satisfies readonly ItemStatus[];

export const MAX_IMAGES_PER_ITEM = 10;
/** Largest single photo the server accepts (the phone normally sends ~0.5 MB after resizing). */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
/** Upper bound for any price or offer: $100,000. */
export const MAX_PRICE_CENTS = 10_000_000;

export const TITLE_MAX = 120;
export const DESCRIPTION_MAX = 2000;
export const PASSWORD_MIN = 10;

/** Path segments a sale slug may not take, because the app uses them. */
export const RESERVED_SLUGS = [
  'admin', 'api', 'assets', 'media', 'healthz', 'login', 'logout',
  'static', 'favicon.ico', 'robots.txt', 'sellit', 'www', 'platform-admin', 'privacy', 'start', 'terms', 'demo',
] as const;

export const INQUIRY_STATUSES = ['NEW', 'HANDLED', 'SPAM'] as const;
export type InquiryStatus = (typeof INQUIRY_STATUSES)[number];

export const NAME_MAX = 80;
export const MESSAGE_MAX = 1000;
export const LOCATION_MAX = 120;
export const CONTACT_NOTE_MAX = 500;
export const SALE_DESCRIPTION_MAX = 2000;

/**
 * Sale lifecycle. DRAFT: hidden, members can preview. ACTIVE: published. CLOSED: still visible,
 * marked as ended. ARCHIVED: hidden again. Only a platform admin moves a sale in or out of DRAFT/ARCHIVED.
 */
export const SALE_STATUSES = ['DRAFT', 'ACTIVE', 'CLOSED', 'ARCHIVED'] as const;
export type SaleStatus = (typeof SALE_STATUSES)[number];
/** Statuses the public can see. */
export const PUBLIC_SALE_STATUSES = ['ACTIVE', 'CLOSED'] as const satisfies readonly SaleStatus[];

export const SALE_ROLES = ['OWNER', 'EDITOR'] as const;
export type SaleRole = (typeof SALE_ROLES)[number];

export const USER_STATUSES = ['ACTIVE', 'DISABLED'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** How long an invite or password-reset link works. */
export const SETUP_LINK_HOURS = 72;
export const MAX_SALE_MEMBERS = 20;
export const PRIVATE_ADDRESS_MAX = 300;

export const SELLER_REQUEST_STATUSES = ['NEW', 'INVITED', 'DECLINED', 'SPAM'] as const;
export type SellerRequestStatus = (typeof SELLER_REQUEST_STATUSES)[number];

/**
 * Current versions of the Terms of Service and Privacy Policy (the date each took effect). Raising
 * either one asks every seller to accept again; a wording fix that keeps the version doesn't.
 */
export const TERMS_VERSION = '2026-10-25';
export const PRIVACY_VERSION = '2026-10-25';
